import React from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { BandsTypes } from "../Types";
import { BandingGenderForm } from "../BandingGenderForm/BandingGenderForm.logic";
import { BandingAgeForm } from "../BandingAgeForm/BandingAgeForm.logic";
import { BandingEthnicityForm } from "../BandingEthnicityForm/BandingEthnicityForm.logic";
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogInner,
  DialogHeader,
  DialogContent,
  DialogFooter
} from "../../../../Common/UI/Index";
import { ButtonVariantType } from "../../../../Common/UI/Types";
import "./Style.scss";

interface OutputBandingPopupViewProps {
  /**
   * Close action for popup
   */
  onClosePopup: () => void;
  /**
   * Flag to show the dialog
   */
  showDialog: boolean;
  /**
   * Gender checked values
   */
  value: string[];
  /**
   * Handler for name change
   */
  handleBandNameChange: (name: string) => void;
  /**
   * Flag for valid name
   */
  isValidName: boolean;
  /**
   * Handler for name change
   */
  handleSave: () => void;
  /**
   * Header text
   */
  headerText: string;
  /**
   * Band type
   */
  selectedBandingType: BandsTypes;
  /**
   * Onchange for Band name
   */
  onFilterChange?: (selectedFilter: string) => void;
  /**
   * Flag for ethnicity validations
   */
  isValidEthnic: boolean;
  /**
   * Flag for the valid age band
   */
  isValidAgeBand: boolean;
  /**
   * Entered banding name
   */
  bandingName: string;
}

/**
 * Responsible to render banding popup
 * @returns Banding popup view
 */
export const OutputBandingPopupView: React.FC<OutputBandingPopupViewProps> = (props: OutputBandingPopupViewProps) => {
  const {
    onClosePopup,
    showDialog,
    value,
    handleBandNameChange,
    isValidName,
    handleSave,
    headerText,
    selectedBandingType,
    onFilterChange,
    isValidEthnic,
    isValidAgeBand,
    bandingName
  } = props;
  const { t: translate } = useTranslation();

  const bandingComponent = {
    [BandsTypes.AGE]: <BandingAgeForm onChange={handleBandNameChange} bandingName={bandingName} />,
    [BandsTypes.GENDER]: <BandingGenderForm onChange={handleBandNameChange} bandingName={bandingName} />,
    [BandsTypes.ETHNICITY]:
      <BandingEthnicityForm
        onChange={handleBandNameChange}
        onFilterChange={onFilterChange}
        bandingName={bandingName}
      />
  };

  const validations = {
    [BandsTypes.AGE]: isValidName && isValidAgeBand,
    [BandsTypes.GENDER]: (isValidName && value.length > 0),
    [BandsTypes.ETHNICITY]: isValidName && isValidEthnic
  };

  const getCustomStyleName = (): string => {
    if (selectedBandingType === BandsTypes.AGE) {
      return "output-banding-age";
    } else if (selectedBandingType === BandsTypes.GENDER) {
      return "output-banding-gender";
    } else {
      return "output-banding-ethinicity";
    }
  };

  const customStyleName = getCustomStyleName();
  return (
    <div id="ebt-outputConfig-outputBanding-dialog">
      {showDialog &&
        <Dialog
          open={showDialog}
          onOpenChange={onClosePopup}
          className={customStyleName}
        >
          <DialogInner className={`${customStyleName}-inner`}>
            <DialogHeader
              className={customStyleName + "-header"}
              id="ebt-outputConfig-outputBanding-dialog-header"
            >
              <div>
                {translate(headerText)}
              </div>
            </DialogHeader>
            <DialogContent
              className={customStyleName + "-content"}
              id="ebt-outputConfig-outputBanding-dialog-content"
            >
              <div className="output-config-dialog-banding-content">
                {bandingComponent[selectedBandingType]}
              </div>
            </DialogContent>
            <DialogFooter
              className={customStyleName + "-footer"}
              id="ebt-outputConfig-outputBanding-dialog-footer"
            >
              <div className="output-config__footer">
                <ButtonGroup className="output-config__buttons_group_right">
                  <Button
                    borderless={true}
                    type="button"
                    onClick={onClosePopup}
                    id="ebt-outputConfig-outputBanding-cancel-btn"
                  >
                    {translate("Button.Cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant={ButtonVariantType.FILLED}
                    onClick={handleSave}
                    disabled={!validations[selectedBandingType]}
                    id="ebt-outputConfig-outputBanding-save-btn"
                  >
                    {translate("Button.Save")}
                  </Button>
                </ButtonGroup>
              </div>
            </DialogFooter>
          </DialogInner>
        </Dialog>
      }
    </div>
  );
};
